// SignInHost — the small host that drives the pure `SignIn` surface.
//
// Owns the one view-ephemeral bit (the failure notice, dismissed on retry) and
// clears `?signin_error` from the address bar so a reload does not replay it.
// Sign-in itself is a full-page navigation to Auth0, injected as
// `onExternalLogin` (email path) and `onProviderLogin` (straight to Google /
// Microsoft) from the IAM port.

export type { SignInHostProps };
export { SignInHost };

import type { LoginHint } from "@bilda/app/loginHint";
import { type SignInErrorKind, signInErrorParam } from "@bilda/app/signInError";
import type { SignInProvider } from "@bilda/app/signInProvider";
import { useEffect, useState } from "react";
import { SignIn } from "./SignIn.js";

type SignInHostProps = {
    readonly onExternalLogin: (loginHint?: LoginHint) => void;
    readonly onProviderLogin: (provider: SignInProvider) => void;
    // Optional escape hatch out of the pre-auth gate (F1).
    readonly onBack?: () => void;
    // Why the previous attempt failed (parsed from the URL at the shell
    // boundary, FR-1.4). Shown until the visitor retries.
    readonly failure?: SignInErrorKind;
};

const SignInHost = ({
    onExternalLogin,
    onProviderLogin,
    onBack,
    failure,
}: SignInHostProps) => {
    // View-ephemeral notice (never survives a reload, no domain meaning).
    const [shownFailure, setShownFailure] = useState(failure);
    useEffect(() => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(signInErrorParam)) return;
        url.searchParams.delete(signInErrorParam);
        window.history.replaceState(null, "", url);
    }, []);
    const onRetry = () => {
        setShownFailure(undefined);
        onExternalLogin();
    };
    const onSignIn = (loginHint?: LoginHint) => onExternalLogin(loginHint);
    return (
        <SignIn
            onSignIn={onSignIn}
            onSignInWith={onProviderLogin}
            onBack={onBack}
            failure={shownFailure}
            onRetry={onRetry}
        />
    );
};
